import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { List, fromJS } from 'immutable';
import { DATA_WELL } from 'reporting-data/constants/chartTypes';
import { PRIOR_PERIOD } from 'reporting-data/constants/compareTypes';
import { AGGREGATION } from 'reporting-data/constants/configTypes';
import { SUM } from 'reporting-data/constants/metricTypes';
import { getDisplayParamsSubAppKey, getReportConfig } from 'reporting-data/report/configReportGetters';
import { getCustomWidgetType } from 'reporting-data/report/reportGetters';
import { CUSTOM_WIDGET_TYPES } from 'reporting-data/tsTypes/customWidgetTypes';
import { SUB_APPS } from 'reporting-data/tsTypes/subAppTypes';
import { Well, WellBigNumber, WellItem, WellLabel } from 'foundations-components/transitional/components/Well';
import { RANGE_TYPES } from 'reporting-data/constants/dateRangeTypes';
// @ts-expect-error Untyped dependency
import makeDateRangeByType from 'reporting-data/lib/makeDateRangeByType';
import PlatformReportResolver from 'reporting-ui-components/components/PlatformReport/PlatformReportResolver';
import { InlineLabels } from 'reporting-ui-components/components/inlineLabels/InlineLabels';
import { INLINE_LABEL_TYPES } from 'reporting-ui-components/components/inlineLabels/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const METRIC_KEYS = ['metrics_primary', 'metrics_secondary'];
const getKpiValueFromResolveData = (resolveData, metricKey) => {
  const metric = resolveData.getIn(['summary', 'data', 'data', 0, metricKey]);
  if (metric == null) {
    return null;
  }
  return resolveData.getIn(['summary', 'data', 'properties', metricKey, 'references', String(metric), 'label']);
};
const CUSTOM_KPI_WIDGETS = [CUSTOM_WIDGET_TYPES.CHANGE_HISTORY_DEAL_LIST, CUSTOM_WIDGET_TYPES.DEAL_PUSH_ANALYSIS, CUSTOM_WIDGET_TYPES.SALES_QUOTA, CUSTOM_WIDGET_TYPES.WATERFALL];
const PLATFORM_RESOLVED_KPI_WIDGETS = [CUSTOM_WIDGET_TYPES.ENGAGEMENT_STREAM];
export const hasPlatformResolverKpi = report => {
  const customWidgetType = getCustomWidgetType(report);
  const subAppKey = getDisplayParamsSubAppKey(report);
  return !!customWidgetType && (PLATFORM_RESOLVED_KPI_WIDGETS.includes(customWidgetType) || subAppKey === SUB_APPS.salesBySource);
};
export const hasCustomWidgetKpis = report => {
  const customWidgetType = getCustomWidgetType(report);
  if (!customWidgetType) {
    return false;
  }
  const subAppKey = getDisplayParamsSubAppKey(report);
  return CUSTOM_KPI_WIDGETS.includes(customWidgetType) && subAppKey !== SUB_APPS.salesBySource || hasPlatformResolverKpi(report);
};
const getCustomKpis = (reportResolve, isPreview) => {
  const resolveData = reportResolve.data;
  if (!resolveData.get('summary')) {
    return [];
  }
  const customKpis = METRIC_KEYS.reduce((memo, metricKey) => {
    const metricValue = getKpiValueFromResolveData(resolveData, metricKey);
    if (metricValue) {
      memo.push({
        langKey: metricKey,
        value: metricValue
      });
    }
    return memo;
  }, []);
  return isPreview && customKpis.length > 0 ? customKpis.slice(0, 1) : customKpis;
};
const widgetKpiProperty = {
  [CUSTOM_WIDGET_TYPES.ENGAGEMENT_STREAM]: 'count'
};
const getPlatformResolverKpi = (report, isPreview) => {
  const customWidgetType = getCustomWidgetType(report);
  const subAppKey = getDisplayParamsSubAppKey(report);
  let kpiReport = null;
  switch (customWidgetType) {
    case CUSTOM_WIDGET_TYPES.ENGAGEMENT_STREAM:
      {
        kpiReport = report.setIn(['config', 'metrics'], fromJS([{
          metricTypes: [SUM],
          property: widgetKpiProperty[customWidgetType]
        }])).setIn(['config', 'dimensions'], List()).set('chartType', DATA_WELL).setIn(['config', 'configType'], AGGREGATION).deleteIn(['displayParams', 'customWidget']).setIn(['config', 'compare'], PRIOR_PERIOD);
        break;
      }
    default:
      break;
  }
  if (subAppKey === SUB_APPS.salesBySource) {
    kpiReport = report.setIn(['config', 'metrics'], fromJS([{
      metricTypes: [SUM],
      property: 'hs_closed_amount_in_home_currency'
    }])).setIn(['config', 'dimensions'], List(['hs_analytics_source'])).updateIn(['config', 'filters', 'dateRange', 'value'], originalDateRange => fromJS(makeDateRangeByType(originalDateRange.toJS())).set('rangeType', RANGE_TYPES.CUSTOM)).set('chartType', DATA_WELL).setIn(['config', 'configType'], AGGREGATION).setIn(['config', 'limit'], isPreview ? 1 : 3).deleteIn(['displayParams', 'customWidget']).setIn(['config', 'compare'], PRIOR_PERIOD);
  }
  return kpiReport ? /*#__PURE__*/_jsx(PlatformReportResolver, {
    allowDrilldown: !isPreview,
    compact: !isPreview,
    report: kpiReport,
    useInlineLabels: !!isPreview,
    inlineLabelTypes: [INLINE_LABEL_TYPES.DATE],
    enableReportResolveUsageTracking: false
  }) : null;
};
const SalesCustomWidgetKPIContent = ({
  previewReport,
  reportResolve,
  isPreview
}) => {
  if (hasPlatformResolverKpi(previewReport)) {
    return getPlatformResolverKpi(previewReport, isPreview);
  }
  if (!reportResolve) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true
    });
  }
  const kpis = getCustomKpis(reportResolve, isPreview);
  const subAppKey = getDisplayParamsSubAppKey(previewReport);
  if (kpis.length === 0 || !subAppKey) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isPreview && /*#__PURE__*/_jsx(InlineLabels, {
      config: getReportConfig(previewReport),
      isMobile: false,
      inlineLabelTypes: [INLINE_LABEL_TYPES.DATE]
    }), /*#__PURE__*/_jsx("div", {
      style: {
        flex: 1
      },
      children: /*#__PURE__*/_jsx(Well, {
        padding: isPreview ? undefined : '0',
        margin: isPreview ? undefined : '0',
        children: kpis.map(kpi => /*#__PURE__*/_jsxs(WellItem, {
          padding: isPreview ? undefined : '0',
          margin: isPreview ? undefined : '0',
          children: [/*#__PURE__*/_jsx(WellLabel, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `reporting-enablement.singleReportViewer.summaryDatawellInsight.kpis.salesCustomWidgets.${subAppKey}.${kpi.langKey}`
            })
          }), /*#__PURE__*/_jsx(WellBigNumber, {
            children: kpi.value
          })]
        }, kpi.langKey))
      })
    })]
  });
};
export default SalesCustomWidgetKPIContent;
export const __TESTABLE__ = {
  getCustomKpis
};